import type { FC, ReactNode } from 'react';

/**
 * Class name every section of the grid must carry.
 *
 * The two sections have to line up row by row - the designs put "Device type breakdown" and
 * "Construct type breakdown" at the same height - but the cloud summary is taller, since it carries
 * the "CCU model" footnote. So a section is not a self-contained column: it spans both rows of the
 * grid and re-uses their tracks (`grid-rows-subgrid`), which makes the summary row as tall as the
 * taller of the two summaries and starts the breakdown row at one height for both columns.
 *
 * `min-w-0` is what keeps a narrow window from breaking the layout: a grid item defaults to
 * `min-width: auto`, so the nowrap summary figures and the chart canvas would push the section wider
 * than its track and overflow the card instead of shrinking with it.
 *
 * The gap here is a fallback: in a subgridded axis the parent grid's gap is what applies, so this
 * value only takes effect where `subgrid` is unsupported. Keep it equal to the grid's row gap.
 */
export const licenseSectionClassName =
  'grid min-w-0 grid-rows-subgrid row-span-2 gap-2xl';

export type LicenseSectionsGridProps = {
  /** Sections, each carrying `licenseSectionClassName`. */
  children: ReactNode;
};

/**
 * The two licensing sections, side by side, sharing a summary row and a breakdown row.
 *
 * Deliberately not responsive - the app does not support small screens, so the columns stay side by
 * side at every width and simply get narrower.
 */
export const LicenseSectionsGrid: FC<LicenseSectionsGridProps> = ({
  children,
}) => (
  <div className='grid grid-cols-2 grid-rows-[auto_1fr] gap-x-none gap-y-2xl'>
    {children}
  </div>
);
